import { useTranslation } from "react-i18next";
import { Cake } from "lucide-react";
import { Card, Typography } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { StayPack } from "@/hooks/useStayPacks";
import { isStayPackActiveOnDate } from "../reservation/useReservationPacks";
import {
  birthdayPetsOnDate,
  countsOnDate,
  monthGridCells,
  parseIsoDate,
  type PlanBirthdayPet,
  type ScheduleReservation,
  type StayShowFilter,
} from "./scheduleModel";

interface MonthGridProps {
  anchor: Date;
  packs: StayPack[];
  reservations: ScheduleReservation[];
  planBirthdayPets: PlanBirthdayPet[];
  selectedDate: string;
  show: StayShowFilter;
  onSelectDate: (isoDate: string) => void;
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

export default function MonthGrid({ anchor, packs, reservations, planBirthdayPets, selectedDate, show, onSelectDate }: MonthGridProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const cells = monthGridCells(anchor);

  return (
    <Card className="mb-[18px] p-2.5 sm:p-[18px]">
      <div className="mb-2 grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEKDAY_KEYS.map((key) => (
          <div key={key} className="text-center font-body text-[10px] font-semibold uppercase tracking-[0.05em] text-neutral-400 sm:text-[11px]">
            {t(`schedule.weekdayShort.${key}`)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((isoDate, index) => {
          if (!isoDate) return <div key={`blank-${index}`} className="min-h-[52px] sm:min-h-[76px]" />;

          const selected = isoDate === selectedDate;
          const counts = countsOnDate(reservations, isoDate, show);
          const dayPacks = packs.filter((pack) => isStayPackActiveOnDate(pack, isoDate));
          const dayBirthdays = birthdayPetsOnDate(reservations, planBirthdayPets, isoDate, show);

          return (
            <div key={isoDate} className="relative">
              <button
                type="button"
                onClick={() => onSelectDate(isoDate)}
                aria-pressed={selected}
                aria-label={t("schedule.dayCellLabel", {
                  date: parseIsoDate(isoDate).toLocaleDateString(locale),
                  daycare: counts.daycare,
                  hotel: counts.hotel,
                })}
                className={`flex min-h-[52px] w-full flex-col gap-0.5 rounded-md border-[1.5px] p-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-[96px] sm:gap-1 sm:p-[7px_8px] ${
                  selected ? "border-primary bg-card" : "border-border bg-card hover:bg-muted"
                }`}
              >
                <div className={`font-body text-[11px] pr-2.5 sm:pr-0 sm:text-[12.5px] ${selected ? "font-bold text-primary" : "font-medium text-foreground"}`}>
                  {parseIsoDate(isoDate).getDate()}
                </div>

                {counts.daycare > 0 && (
                  <div className="inline-flex w-full items-center justify-center rounded-sm bg-orange-50 px-1 py-0.5 font-body text-[9.5px] font-bold text-orange-600 sm:justify-start sm:gap-1 sm:px-1.5 sm:text-[10.5px] sm:font-semibold">
                    <span className="sm:hidden">{counts.daycare}</span>
                    <span className="hidden sm:inline">{t("schedule.countDaycare", { count: counts.daycare })}</span>
                  </div>
                )}
                {counts.hotel > 0 && (
                  <div className="inline-flex w-full items-center justify-center rounded-sm bg-purple-50 px-1 py-0.5 font-body text-[9.5px] font-bold text-purple-700 sm:justify-start sm:gap-1 sm:px-1.5 sm:text-[10.5px] sm:font-semibold">
                    <span className="sm:hidden">{counts.hotel}</span>
                    <span className="hidden sm:inline">{t("schedule.countHotel", { count: counts.hotel })}</span>
                  </div>
                )}

                <div className="mt-auto hidden items-center gap-[3px] pt-[3px] sm:flex">
                  {dayPacks.map((pack) => (
                    <span
                      key={pack.id}
                      title={pack.name}
                      className="h-[7px] w-[7px] shrink-0 rounded-full bg-brand-purple"
                      style={pack.color ? { background: pack.color } : undefined}
                    />
                  ))}
                </div>
              </button>

              {dayBirthdays.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onClick={(event) => event.stopPropagation()}
                      aria-label={t("schedule.birthdayIconLabel", { count: dayBirthdays.length })}
                      className="absolute right-1 top-1.5 rounded-full bg-pink-100 p-0.5 text-pink-600 transition-colors hover:bg-pink-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:right-2 sm:top-1.5 sm:p-1"
                    >
                      <Cake aria-hidden="true" size={8} className="sm:hidden" />
                      <Cake aria-hidden="true" size={12} className="hidden sm:block" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56">
                    <Typography variant="caption" className="mb-1.5 block font-semibold text-foreground">
                      {t("schedule.birthdayPopoverTitle")}
                    </Typography>
                    <ul className="flex flex-col gap-1">
                      {dayBirthdays.map((reservation) => (
                        <li key={reservation.petId} className="text-sm">
                          <span className="font-medium">{reservation.petName}</span>
                          <span className="text-muted-foreground"> · {reservation.ownerName}</span>
                        </li>
                      ))}
                    </ul>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
